"use client";

// V2 Step 11a — FS Access permission decay banner.
//
// Single component, two render modes via vaultCtx.permissionState:
//   "decayed" — Chrome silently revoked perms after ~2 weeks of inactivity.
//                Spec copy + [Reconnect] button.
//   "blocked" — User explicitly blocked folder access.
//                Spec copy + [Try Again] button (cloud-only fallback noted).
//
// Mounted via BannerSlot (registry priority 5). Condition gates on
// vaultCtx.permissionState being "decayed" or "blocked"; component returns
// null otherwise as defense-in-depth (matches LapsedBanner internal check
// pattern).
//
// On click, fires vaultCtx.retryPermission() — Chrome requires
// requestPermission() to fire within a user gesture, so this MUST come
// from a click handler. retryPermission handles signal emission + state
// transitions; this component is purely presentational + click-router.
//
// On grant: permissionState transitions to "granted", banner condition
// fails, banner unmounts naturally via BannerSlot re-evaluation. No
// internal "hide" state needed.
//
// Companion to V2MigrationModal (10d) and V2CloudOnlyBanner (10e).

import { useState } from "react";
import { useVaultContext } from "../lib/vault";

export default function V2PermissionBanner() {
  const { permissionState, retryPermission } = useVaultContext();
  const [busy, setBusy] = useState(false);

  // Defense-in-depth: registry condition gates entry, but if context
  // somehow renders us in a non-decayed/blocked state, return null.
  if (permissionState !== "decayed" && permissionState !== "blocked") return null;

  const isBlocked = permissionState === "blocked";

  // V2 spec copy verbatim — md/vault/vault-rearchitecture-v2.md
  // line 755-758 (decay) + 766-769 (blocked).
  const message = isBlocked
    ? "Folder access blocked. Working in cloud-only — drops save to cloud and sync once you reconnect."
    : "Chrome paused access to your vault folder — security feature for inactive sessions. One click to reconnect.";
  const buttonLabel = isBlocked ? "Try Again" : "Reconnect";
  const ariaLabel = isBlocked
    ? "Folder access blocked — working cloud-only, click Try Again to reconnect"
    : "Vault folder access paused — click Reconnect";

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await retryPermission();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="complementary"
      aria-label={ariaLabel}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)",
        background: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border-light)",
        borderRadius: "var(--radius-md)",
        marginBottom: "var(--space-4)",
        fontFamily: "var(--font-primary)",
      }}
    >
      <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text)", lineHeight: 1.5 }}>
        {message}
      </div>
      <button
        onClick={handleClick}
        disabled={busy}
        style={{
          padding: "var(--space-2) var(--space-4)",
          background: "var(--color-accent)",
          color: "var(--color-text-inverse)",
          border: "none",
          borderRadius: "var(--radius-md)",
          fontSize: "var(--font-size-sm)",
          fontWeight: "var(--font-weight-semibold)",
          fontFamily: "var(--font-primary)",
          cursor: busy ? "not-allowed" : "pointer",
          flexShrink: 0,
        }}
      >
        {busy ? "…" : buttonLabel}
      </button>
    </div>
  );
}
